import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { RefreshService } from '../../core/refresh.service';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { UserService } from '../../core/user';
import { UserLocation } from '../../models/user/location.model';
import { NavbarConstants } from './navbar.constants';
import { SharedConstants } from '../shared.constants';
import { AnalyticsService } from '../../core/analytics';
import * as fromRoot from '../../reducers';
import { NavigationService } from '../../navigation.service';
import { buildUrlForLocationChange } from './location-utils';
import { LocationPickerBase } from './location-picker.base';

@Component({
  selector: 'navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NavBarComponent extends LocationPickerBase implements OnInit, OnDestroy {
  @Input() public showLocationNumber: boolean = false;
  @Input() public showSelectedLocationName: boolean = false;
  @Input() public selectedIndex: number = 0;
  /** When false, hide Engage tab links / mobile footer and keep location controls only. */
  @Input() public showNavLinks: boolean = true;

  public disableDiscoverTab$: Observable<boolean>;
  public disableMeetTab$: Observable<boolean>;
  public disableRecoverTab$: Observable<boolean>;
  public disableEngageTab$: Observable<boolean>;
  public disableCalendarTab$: Observable<boolean>;
  public navLinks: any[];
  public isMobile$: Observable<boolean>;
  public unreadNotificationCount$: Observable<number>;
  public isDarkMode$: Observable<boolean>;
  public flagSub: Subscription = null;
  protected readonly location = location;
  public isNextGen$: Observable<boolean>;
  public readonly lightLogoUrl = 'https://www.cfacdn.com/img/Spotlight/Icons/SpotlightFixNavLogo.svg';
  public readonly darkLogoUrl = 'assets/img/spotlight-logo-dark.svg';

  constructor(private analytics: AnalyticsService,
              private us: UserService,
              private router: Router,
              private sanitizer: DomSanitizer,
              private navService: NavigationService,
              private readonly refreshService: RefreshService,
              store: Store<fromRoot.State>) {
    super(store);
  }

  public override ngOnInit() {
    super.ngOnInit();

    this.disableDiscoverTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableDiscoverTabFlag));
    this.disableMeetTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableMeetTabFlag));
    this.disableRecoverTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableRecoverTabFlag));
    this.disableEngageTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableEngageTabFlag));
    this.disableCalendarTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableCalendarTabFlag));
    this.isNextGen$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.displaySidenav));
    this.isDarkMode$ = this.store.select(fromRoot.getDarkModeEnabled);

    this.flagSub = combineLatest([
      this.disableDiscoverTab$,
      this.disableMeetTab$,
      this.disableRecoverTab$,
      this.disableEngageTab$,
      this.disableCalendarTab$
    ]).pipe(take(1)).subscribe(([
      disableDiscoverTab,
      disableMeetTab,
      disableRecoverTab,
      disableEngageTab,
      disableCalendarTab
    ]) => {
      this.navLinks = NavbarConstants.navbarTabs(
        disableDiscoverTab,
        disableMeetTab,
        disableRecoverTab,
        disableEngageTab,
        disableCalendarTab
      );
      this.navLinks[this.selectedIndex].active = true;
    });

    this.unreadNotificationCount$ = this.store.select(fromRoot.getNotificationUnreadCount);
    this.isMobile$ = this.store.select(fromRoot.getIsMobile);
  }

  public updateLocation(location, isNextGen: boolean) {
    const matched = this.locations?.find(
      (loc) => loc.locationNumber === location.locationNumber,
    ) ?? location;
    this.selectedLocation = location
    this.analytics.sendEvent(
      'Navbar Location Change',
      {
        'Location #': matched.locationNumber,
        'Location Name': matched.locationName
      }
    );
    const reason = isNextGen ? 'location-change' : 'location-change-legacy';
    this.refreshService.setRefreshReason(reason);
    globalThis.location.href = buildUrlForLocationChange(location.locationNumber);
  }

  public override toggleFavorite(location: UserLocation): void {
    const isCurrentlyFavorite = this.favoriteLocationNumbers.includes(location.locationNumber);
    this.analytics.sendEvent(
      isCurrentlyFavorite ? 'Unfavorite Location' : 'Favorite Location',
      {
        'Location #': location.locationNumber,
        'Location Name': location.locationName
      }
    );
    super.toggleFavorite(location);
  }

  public reloadApp(): void {
    globalThis.location.reload();
  }

  public openSettings() {
    this.analytics.sendEvent('Settings Click', {});
    this.router.navigate(['/settings']);
  }

  public openNotifications() {
    this.analytics.sendEvent('Notifications Click', {});
    this.router.navigate(['/notifications']);
  }

  public logoutUser() {
    this.analytics.sendEvent('Logout Click', {});
    this.navService.signOut();
  }

  public stateDisabled() {
    return !this.locations || (this.locations && this.locations.length <= 1);
  }

  public navigateToMenu() {
    if (this.shouldUseLocationSearch(this.locations ?? [], this.pageSize)) {
      const previousUrl = window.location.href.substring(window.location.href.lastIndexOf('#') + 1);
      this.router.navigate(
        ['menu/location-search'],
        { queryParams: { previousUrl: previousUrl } }
      );
    } else {
      this.router.navigate(['/menu']);
    }
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.flagSub) {
      this.flagSub.unsubscribe();
    }
  }
}
